import { useState, useEffect } from 'react';
import DashboardLayout from '../dashboard/components/DashboardLayout';
import { OrderList } from './components/OrderList';
import { OrderForm } from './components/OrderForm';
import { OrderDetail } from './components/OrderDetail';
import { supabase } from '../../lib/supabase';

interface Order {
  id: string;
  orderNumber: string;
  createdBy: string;
  createdByName: string;
  createdByRole: 'Manager' | 'Partner';
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  notes: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'processing' | 'shipping' | 'delivered' | 'returned' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  color: string;
  size: string;
  quantity: number;
  price: number;
  image?: string;
}

// Mock data for fallback
const mockOrders: Order[] = [];

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [currentUser] = useState({
    id: 'user1',
    name: 'Nguyễn Văn A',
    role: 'Manager' as 'Manager' | 'Partner',
    commissionRate: 10
  });

  // Load orders from Supabase
  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Đơn giản hóa query để tránh infinite recursion với bảng users
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          customer_name,
          customer_phone,
          customer_address,
          total_amount,
          status,
          notes,
          created_at,
          updated_at,
          created_by,
          partner_id,
          order_items (
            id,
            product_id,
            quantity,
            unit_price,
            total_price,
            products (
              name,
              images
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedOrders: Order[] = (data || []).map(order => ({
        id: order.id,
        orderNumber: order.order_number || `ORD-${order.id.slice(0, 8)}`,
        createdBy: order.created_by || 'unknown',
        createdByName: 'Người dùng', // Sử dụng tên mặc định để tránh query users
        createdByRole: 'Manager', // Sử dụng role mặc định
        customerName: order.customer_name,
        customerPhone: order.customer_phone,
        customerAddress: order.customer_address,
        notes: order.notes || '',
        items: order.order_items.map((item: any) => ({
          id: item.id,
          productId: item.product_id,
          productName: item.products?.name || 'Sản phẩm không xác định',
          color: 'Không xác định',
          size: 'Không xác định',
          quantity: item.quantity,
          price: item.unit_price,
          image: item.products?.images?.[0] || ''
        })),
        totalAmount: order.total_amount,
        status: order.status,
        createdAt: order.created_at,
        updatedAt: order.updated_at
      }));

      setOrders(transformedOrders);
    } catch (err: any) {
      console.error('Error loading orders:', err);
      console.warn('Lỗi kết nối database, sử dụng dữ liệu mẫu:', err.message);
      // Fallback về mock data khi có lỗi
      setOrders(mockOrders);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleCreateOrder = async (orderData: Partial<Order>) => {
    try {
      setLoading(true);

      // Kiểm tra tồn kho trước khi tạo đơn hàng
      for (const item of orderData.items || []) {
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('stock_quantity')
          .eq('id', item.productId)
          .single();

        if (productError) {
          throw new Error(`Không thể kiểm tra sản phẩm: ${productError.message}`);
        }

        if (!product || product.stock_quantity < item.quantity) {
          throw new Error(`Sản phẩm "${item.productName}" không đủ tồn kho. Còn lại: ${product?.stock_quantity || 0}, yêu cầu: ${item.quantity}`);
        }
      }

      // Tạo đơn hàng
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          customer_name: orderData.customerName,
          customer_phone: orderData.customerPhone,
          customer_address: orderData.customerAddress,
          total_amount: orderData.totalAmount,
          status: orderData.status || 'pending',
          partner_id: orderData.partnerId
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Tạo order items và trừ tồn kho
      for (const item of orderData.items || []) {
        // Tạo order item
        const { error: itemError } = await supabase
          .from('order_items')
          .insert({
            order_id: order.id,
            product_id: item.productId,
            quantity: item.quantity,
            price: item.price,
            image: item.image
          });

        if (itemError) throw itemError;

        // Trừ tồn kho
        const { error: stockError } = await supabase
          .from('products')
          .update({
            stock_quantity: supabase.sql`stock_quantity - ${item.quantity}`
          })
          .eq('id', item.productId);

        if (stockError) {
          console.error('Lỗi cập nhật tồn kho:', stockError);
          // Không throw error để không làm gián đoạn việc tạo đơn hàng
        }
      }

      await loadOrders();
      setShowCreateForm(false);
      console.log('✅ Đơn hàng đã được tạo và tồn kho đã được cập nhật');
    } catch (error: any) {
      console.error('❌ Lỗi tạo đơn hàng:', error);
      setError(error.message || 'Có lỗi xảy ra khi tạo đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async (orderId: string, updates: Partial<Order>) => {
    try {
      setLoading(true);

      // Lấy thông tin đơn hàng cũ để so sánh
      const { data: oldOrder, error: oldOrderError } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            product_id,
            quantity,
            price,
            image
          )
        `)
        .eq('id', orderId)
        .single();

      if (oldOrderError) throw oldOrderError;

      // Cập nhật đơn hàng
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          customer_name: updates.customerName,
          customer_phone: updates.customerPhone,
          customer_address: updates.customerAddress,
          total_amount: updates.totalAmount,
          status: updates.status,
          partner_id: updates.partnerId
        })
        .eq('id', orderId);

      if (updateError) throw updateError;

      // Nếu đơn hàng bị hủy, hoàn lại tồn kho
      if (updates.status === 'cancelled' && oldOrder.status !== 'cancelled') {
        for (const item of oldOrder.order_items || []) {
          const { error: stockError } = await supabase
            .from('products')
            .update({
              stock_quantity: supabase.sql`stock_quantity + ${item.quantity}`
            })
            .eq('id', item.product_id);

          if (stockError) {
            console.error('Lỗi hoàn lại tồn kho:', stockError);
          }
        }
        console.log('✅ Đã hoàn lại tồn kho cho đơn hàng bị hủy');
      }

      // Nếu đơn hàng được khôi phục từ trạng thái hủy, trừ lại tồn kho
      if (oldOrder.status === 'cancelled' && updates.status !== 'cancelled') {
        for (const item of oldOrder.order_items || []) {
          // Kiểm tra tồn kho trước khi trừ
          const { data: product, error: productError } = await supabase
            .from('products')
            .select('stock_quantity')
            .eq('id', item.product_id)
            .single();

          if (productError) {
            throw new Error(`Không thể kiểm tra sản phẩm: ${productError.message}`);
          }

          if (!product || product.stock_quantity < item.quantity) {
            throw new Error(`Không đủ tồn kho để khôi phục đơn hàng. Sản phẩm còn lại: ${product?.stock_quantity || 0}, yêu cầu: ${item.quantity}`);
          }

          const { error: stockError } = await supabase
            .from('products')
            .update({
              stock_quantity: supabase.sql`stock_quantity - ${item.quantity}`
            })
            .eq('id', item.product_id);

          if (stockError) {
            console.error('Lỗi trừ tồn kho:', stockError);
          }
        }
        console.log('✅ Đã trừ tồn kho cho đơn hàng được khôi phục');
      }

      await loadOrders();
      setSelectedOrder(null);
      setShowEditForm(false);
    } catch (error: any) {
      console.error('❌ Lỗi cập nhật đơn hàng:', error);
      setError(error.message || 'Có lỗi xảy ra khi cập nhật đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa đơn hàng này?')) return;

    try {
      setLoading(true);

      // Lấy thông tin đơn hàng để hoàn lại tồn kho
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            product_id,
            quantity,
            price,
            image
          )
        `)
        .eq('id', orderId)
        .single();

      if (orderError) throw orderError;

      // Hoàn lại tồn kho nếu đơn hàng chưa bị hủy
      if (order.status !== 'cancelled') {
        for (const item of order.order_items || []) {
          const { error: stockError } = await supabase
            .from('products')
            .update({
              stock_quantity: supabase.sql`stock_quantity + ${item.quantity}`
            })
            .eq('id', item.product_id);

          if (stockError) {
            console.error('Lỗi hoàn lại tồn kho:', stockError);
          }
        }
        console.log('✅ Đã hoàn lại tồn kho cho đơn hàng bị xóa');
      }

      // Xóa order items trước
      const { error: deleteItemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderId);

      if (deleteItemsError) throw deleteItemsError;

      // Xóa đơn hàng
      const { error: deleteOrderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (deleteOrderError) throw deleteOrderError;

      await loadOrders();
      setSelectedOrder(null);
    } catch (error: any) {
      console.error('❌ Lỗi xóa đơn hàng:', error);
      setError(error.message || 'Có lỗi xảy ra khi xóa đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: Order['status']) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) throw error;

      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus, updatedAt: new Date().toISOString() }
          : order
      ));
    } catch (err: any) {
      console.error('Error updating order status:', err);
      setError(err.message || 'Có lỗi xảy ra khi cập nhật trạng thái');
    }
  };

  const confirmDeleteOrder = (order: Order) => {
    setOrderToDelete(order);
    setShowDeleteModal(true);
  };

  const canDeleteOrder = (order: Order): boolean => {
    // Chỉ Manager mới có thể xóa đơn hàng
    // Và chỉ có thể xóa đơn hàng có trạng thái pending hoặc cancelled
    return currentUser.role === 'Manager' && (order.status === 'pending' || order.status === 'cancelled');
  };

  const canEditOrder = (order: Order): boolean => {
    const now = new Date();
    const createdAt = new Date(order.createdAt);
    const hoursDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
    
    // Có thể chỉnh sửa nếu:
    // 1. Trong vòng 24h và trạng thái là pending
    // 2. Hoặc trạng thái là pending (bất kể thời gian nếu chưa xử lý)
    return (hoursDiff < 24 && order.status === 'pending') || order.status === 'pending';
  };

  const getFilteredOrders = () => {
    if (currentUser.role === 'Manager') {
      return orders; // Manager xem tất cả đơn hàng
    } else {
      return orders.filter(order => order.createdBy === currentUser.id); // Partner chỉ xem đơn hàng của mình
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Đang tải dữ liệu...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Có lỗi xảy ra</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadOrders}
            className="bg-gray-900 text-white hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
          >
            Thử lại
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Quản lý đơn hàng</h1>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
              {currentUser.role === 'Manager' 
                ? 'Quản lý tất cả đơn hàng trong hệ thống'
                : 'Quản lý đơn hàng của bạn'
              }
            </p>
          </div>
          
          <button
            onClick={() => setShowForm(true)}
            className="bg-gray-900 hover:bg-gray-800 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap w-full sm:w-auto"
          >
            <i className="ri-add-circle-line text-lg sm:text-xl"></i>
            <span className="text-sm sm:text-lg">Tạo đơn hàng mới</span>
          </button>
        </div>

        {showForm && (
          <OrderForm
            onSubmit={handleCreateOrder}
            onCancel={() => setShowForm(false)}
            currentUser={currentUser}
          />
        )}

        {editingOrder && (
          <OrderForm
            order={editingOrder}
            onSubmit={(data) => handleUpdateOrder(editingOrder.id, data)}
            onCancel={() => setEditingOrder(null)}
            currentUser={currentUser}
            isEditing={true}
          />
        )}

        {selectedOrder && (
          <OrderDetail
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onEdit={canEditOrder(selectedOrder) ? () => {
              setEditingOrder(selectedOrder);
              setSelectedOrder(null);
            } : undefined}
            onStatusChange={handleStatusChange}
            currentUser={currentUser}
            canEdit={canEditOrder(selectedOrder)}
          />
        )}

        {showDeleteModal && orderToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-delete-bin-line text-red-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Xác nhận xóa đơn hàng</h3>
                  <p className="text-sm text-gray-600">Hành động này không thể hoàn tác</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Bạn có chắc chắn muốn xóa đơn hàng <strong>{orderToDelete.orderNumber}</strong> của khách hàng <strong>{orderToDelete.customerName}</strong>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Tổng giá trị: {orderToDelete.totalAmount.toLocaleString('vi-VN')}đ
                </p>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setOrderToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors whitespace-nowrap"
                >
                  Hủy
                </button>
                <button
                  onClick={() => handleDeleteOrder(orderToDelete.id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  Xóa đơn hàng
                </button>
              </div>
            </div>
          </div>
        )}

        <OrderList
          orders={getFilteredOrders()}
          onViewOrder={setSelectedOrder}
          onEditOrder={(order) => {
            if (canEditOrder(order)) {
              setEditingOrder(order);
            }
          }}
          onDeleteOrder={confirmDeleteOrder}
          onStatusChange={handleStatusChange}
          currentUser={currentUser}
          canEditOrder={canEditOrder}
          canDeleteOrder={canDeleteOrder}
        />
      </div>
    </DashboardLayout>
  );
}
